import React, { useState, useEffect } from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';

export default function OrgUnitLevelModal({
    isOpen,
    onClose,
    onSave,
    editData,
    title,
    isLoading = false,
    isViewMode = false
}) {
    const { data: orgsData, isLoading: orgsLoading } = useOrganizations();
    const organizations = orgsData?.results || orgsData || [];

    const [formData, setFormData] = useState({
        org: '',
        name: '',
        rank: '',
        description: '',
        is_active: true
    });

    useEffect(() => {
        if (editData) {
            setFormData({
                org: editData.org_id || editData.org?.id || editData.org || '',
                name: editData.name || '',
                rank: editData.rank || '',
                description: editData.description || '',
                is_active: editData.is_active ?? true
            });
        } else {
            setFormData({
                org: '',
                name: '',
                rank: '',
                description: '',
                is_active: true
            });
        }
    }, [editData, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed p-15 w-full max-w-[600px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]">
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                        {title}
                    </h5>
                    <form onSubmit={handleSubmit}>
                        <div className='my-10 md:px-10 px-[7px] max-h-[65vh] overflow-auto cus-scrollbar'>
                            <div className='grid grid-cols-12 gap-20'>

                                {/* Organization */}
                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Organization {!isViewMode && <span className='text-danger'>*</span>}
                                    </label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm'>
                                            {organizations.find(o => String(o.id) === String(formData.org))?.name || '-'}
                                        </div>
                                    ) : (
                                        <select
                                            name='org'
                                            value={formData.org}
                                            onChange={handleChange}
                                            className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                                            required
                                            disabled={isLoading || orgsLoading}
                                        >
                                            <option value=''>Select Organization</option>
                                            {organizations.map(org => (
                                                <option key={org.id} value={org.id}>{org.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {/* Name */}
                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Name {!isViewMode && <span className='text-danger'>*</span>}
                                    </label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm'>
                                            {formData.name || '-'}
                                        </div>
                                    ) : (
                                        <input
                                            type='text'
                                            name='name'
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder='e.g. Manager'
                                            required
                                            disabled={isLoading}
                                            className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                                        />
                                    )}
                                </div>

                                {/* Rank */}
                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Rank {!isViewMode && <span className='text-danger'>*</span>}
                                    </label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm'>
                                            {formData.rank || '-'}
                                        </div>
                                    ) : (
                                        <>
                                            <select
                                                name='rank'
                                                value={formData.rank}
                                                onChange={handleChange}
                                                className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none'
                                                required
                                                disabled={isLoading}
                                            >
                                                <option value=''>Select Rank</option>
                                                {Array.from({ length: 100 }, (_, i) => i + 1).map(num => (
                                                    <option key={num} value={num}>{num}</option>
                                                ))}
                                            </select>
                                            <small className='text-text-secondary mt-1'>
                                                Higher number = higher authority (e.g., CEO=1, Manager=10)
                                            </small>
                                        </>
                                    )}
                                </div>

                                {/* Description */}
                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Description</label>
                                    {isViewMode ? (
                                        <div className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[80px] rounded-[4px] text-sm'>
                                            {formData.description || '-'}
                                        </div>
                                    ) : (
                                        <textarea
                                            name='description'
                                            value={formData.description}
                                            onChange={handleChange}
                                            placeholder='Description of responsibilities...'
                                            rows={3}
                                            disabled={isLoading}
                                            className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[80px] rounded-[4px] placeholder:text-text-secondary focus:outline-none resize-none'
                                        />
                                    )}
                                </div>

                                {/* Is Active */}
                                <div className='form-group flex flex-col col-span-12'>
                                    {isViewMode ? (
                                        <>
                                            <label className='mb-5 text-sm font-medium'>Status</label>
                                            <div className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm'>
                                                {formData.is_active ? 'Active' : 'Inactive'}
                                            </div>
                                        </>
                                    ) : (
                                        <label className='flex items-center gap-2 cursor-pointer'>
                                            <input
                                                type='checkbox'
                                                name='is_active'
                                                checked={formData.is_active}
                                                onChange={handleChange}
                                                className='w-4 h-4'
                                                disabled={isLoading}
                                            />
                                            <span className='text-sm font-medium'>Is Active</span>
                                        </label>
                                    )}
                                </div>

                            </div>
                        </div>
                        <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='btn btn-outline-secondary'
                                disabled={isLoading}
                            >
                                {isViewMode ? 'Close' : 'Cancel'}
                            </button>
                            {!isViewMode && (
                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : (editData ? 'Update' : 'Submit')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            <div onClick={onClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}
